// cm-coding: Connector administrators configure Workday credentials, bounded
// requisition scope, and display fields here without loading provider records.
// Saving only settings keeps attachment-heavy SOAP retrieval in the explicit
// background pull job and prevents the web request from becoming an unbounded
// preview.

import { useEffect, useState } from 'react'
import { BriefcaseBusiness, Loader2, Trash2 } from 'lucide-react'

import type { NodeApiClient } from '@/app-shell/api/node-api-client'
import {
  Button,
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  Input,
  Label,
} from '@/components/ui'
import { ConnectorDialogSection } from '../../components/ConnectorDialogSection'
import {
  ConnectorSourceDisplayFieldSelection,
  orderedSourceDisplayFields,
} from '../../components/ConnectorSourceDisplayFieldSelection'
import type { ConnectorFeedFor } from '../../types'
import { useWorkdayFeedForm } from '../hooks/useWorkdayFeedForm'
import { WorkdayScope } from './WorkdayScope'

export function WorkdayFeedDialog({
  api,
  orgId,
  vaultId,
  feed,
  canEditFilters,
  canManageCredentials,
  open,
  onOpenChange,
  onConfigured,
}: {
  api: NodeApiClient
  orgId: string
  workspaceId: string
  vaultId: string
  feed?: ConnectorFeedFor<'workday'> | null
  // Server-derived Workday query capability; replaces the old global-admin
  // check now that filter edits require a connector administrator grant.
  canEditFilters: boolean
  canManageCredentials: boolean
  open: boolean
  onOpenChange: (open: boolean) => void
  onConfigured: () => Promise<void>
}) {
  const form = useWorkdayFeedForm({
    api,
    orgId,
    vaultId,
    feed,
    open,
    canAdministerWorkspace: canManageCredentials,
    onConfigured,
  })
  const [sections, setSections] = useState({
    connection: false,
    scope: true,
    display: false,
  })
  useEffect(() => {
    if (!open) return
    setSections({ connection: false, scope: true, display: false })
  }, [feed, open])
  const setSection = (key: keyof typeof sections, value: boolean) => {
    setSections(current => ({ ...current, [key]: value }))
  }
  const configured = form.credentialSource === 'umd'
    ? form.connection.umdConfigured
    : form.connection.workspaceConfigured
  const credentialSources = form.connection.workspaceConfigured === null
    ? (['umd'] as const)
    : (['umd', 'workspace'] as const)
  return (
    <Dialog open={open} onOpenChange={next => { if (!form.saving) onOpenChange(next) }}>
      <DialogContent className="flex max-h-[calc(100vh-2rem)] max-w-[calc(100vw-2rem)] flex-col gap-0 overflow-hidden p-0 sm:max-w-[1120px]">
        <DialogHeader className="shrink-0 border-b border-slate-100 px-6 py-4 pr-12 text-left">
          <div className="flex items-center gap-3">
            <span className="grid h-9 w-9 place-items-center rounded-lg bg-slate-50">
              <BriefcaseBusiness className="h-5 w-5" />
            </span>
            <div>
              <DialogTitle>Workday requisition source</DialogTitle>
              <DialogDescription>
                Imports SRM assessment forms and requisition metadata.
              </DialogDescription>
            </div>
          </div>
        </DialogHeader>
        <div className="min-h-0 flex-1 overflow-y-auto">
          <ConnectorDialogSection
            title="Connection"
            summary={`${form.credentialSource === 'umd' ? 'UMD Credential Store' : 'Workspace credential'} · ${configured ? '✓ stored' : 'not configured'}`}
            open={sections.connection}
            onOpenChange={value => setSection('connection', value)}
          >
            <div className="space-y-4">
              <div className="space-y-1.5">
                <Label htmlFor="workday-api-url">Resource Management API URL</Label>
                <Input
                  id="workday-api-url"
                  type="url"
                  value={form.apiUrl}
                  disabled={!canEditFilters}
                  className="font-mono text-xs"
                  onChange={event => form.setApiUrl(event.target.value)}
                />
              </div>
              <div className={`grid gap-2 ${credentialSources.length > 1 ? 'sm:grid-cols-2' : ''}`}>
                {credentialSources.map(source => (
                  <button
                    key={source}
                    type="button"
                    aria-pressed={form.credentialSource === source}
                    disabled={!canEditFilters}
                    className={`rounded-lg border p-3 text-left ${form.credentialSource === source ? 'border-slate-900' : 'border-slate-200'}`}
                    onClick={() => form.setCredentialSource(source)}
                  >
                    <span className="block text-sm font-medium">
                      {source === 'umd' ? 'UMD Credential Store' : 'Workspace credential'}
                    </span>
                    <span className="text-xs text-slate-500">
                      {source === 'umd'
                        ? 'Managed for this workspace'
                        : 'Encrypted for this workspace'}
                    </span>
                  </button>
                ))}
              </div>
              {form.credentialSource === 'umd' && (
                <div className="rounded-lg border border-slate-200 bg-slate-50 px-3 py-2.5 text-xs text-slate-500">
                  Managed by the credential-store administrator:
                  <div className="mt-1 space-y-0.5 break-all font-mono text-slate-700">
                    {form.connection.umdCredentialKeys.map(key => <div key={key}>{key}</div>)}
                  </div>
                </div>
              )}
              {form.credentialSource === 'workspace' && (
                <div className="grid gap-3 sm:grid-cols-2">
                  <div><Label htmlFor="workday-username">Integration username</Label><Input id="workday-username" autoComplete="username" disabled={!canManageCredentials} value={form.credentials.username} onChange={event => form.setCredentials({ ...form.credentials, username: event.target.value })} /></div>
                  <div><Label htmlFor="workday-password">Password</Label><Input id="workday-password" type="password" autoComplete="new-password" disabled={!canManageCredentials} value={form.credentials.password} onChange={event => form.setCredentials({ ...form.credentials, password: event.target.value })} /></div>
                </div>
              )}
            </div>
          </ConnectorDialogSection>
          <ConnectorDialogSection
            title="Table & scope"
            summary={`Requisitions · ${form.filters.length} filters · specific records`}
            open={sections.scope}
            onOpenChange={value => setSection('scope', value)}
          >
            <WorkdayScope
              filters={form.filters}
              onFiltersChange={form.setFilters}
              canEditFilters={canEditFilters}
            />
          </ConnectorDialogSection>
          <ConnectorDialogSection
            title="Dashboard fields"
            summary={orderedSourceDisplayFields(
              form.sourceDisplayFields,
              form.sourceDisplayFieldKeys,
            ).map(field => field.label).join(', ') || 'No dashboard fields'}
            open={sections.display}
            onOpenChange={value => setSection('display', value)}
          >
            <ConnectorSourceDisplayFieldSelection fields={form.sourceDisplayFields} selectedKeys={form.sourceDisplayFieldKeys} onSelectedKeysChange={form.setSourceDisplayFieldKeys} disabled={!canManageCredentials} />
          </ConnectorDialogSection>
        </div>
        <div className="shrink-0 border-t border-slate-100 bg-white">
          <DialogFooter className="px-6 py-4 sm:justify-between">
            <Button variant="ghost" size="sm" className="text-destructive" disabled={!canManageCredentials || form.credentialSource !== 'workspace' || !form.connection.workspaceConfigured || form.saving} onClick={() => void form.deleteCredential()}><Trash2 className="mr-1 h-3.5 w-3.5" /> Delete stored credential</Button>
            <div className="flex gap-2">
              <Button variant="outline" size="sm" disabled={form.saving} onClick={() => onOpenChange(false)}>Cancel</Button>
              <Button
                size="sm"
                disabled={form.saving || form.loadingConnection}
                onClick={() => void form.submit().then(saved => {
                  if (saved) onOpenChange(false)
                })}
              >
                {form.saving && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
                {form.editing ? 'Save changes' : 'Connect source'}
              </Button>
            </div>
          </DialogFooter>
        </div>
      </DialogContent>
    </Dialog>
  )
}
